import { useEffect, useState } from "react";
import { useSearchParams, Link, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, ExternalLink, Send, ArrowLeft } from "lucide-react";

const INR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export default function Invoices() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: params.get("client") || "", period_month: new Date().toISOString().slice(0, 7), tax_percent: 18 });

  useEffect(() => { if (params.get("new_monthly") === "1") setOpen(true); }, [params]);

  const { data: rows = [] } = useQuery({ queryKey: ["invoices"], queryFn: async () => (await api.get("/invoices")).data });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });

  const genMut = useMutation({
    mutationFn: (p) => api.post("/invoices/generate-monthly", p),
    onSuccess: () => {
      toast.success("Monthly invoice draft created from approved production hours");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const sendMut = useMutation({
    mutationFn: (id) => api.post(`/invoices/${id}/send`),
    onSuccess: () => { toast.success("Invoice sent"); qc.invalidateQueries({ queryKey: ["invoices"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const generate = (e) => {
    e.preventDefault();
    if (!form.client_id || !form.period_month) return toast.error("Client and month are required");
    genMut.mutate({ client_id: form.client_id, period_month: form.period_month, tax_percent: Number(form.tax_percent) || 0 });
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader
        eyebrow="Business Division · Revenue"
        title="Invoices"
        description="Auto-generate from approved production hours or draft manually. Each invoice threads back to the client's approved work."
        actions={
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><Button data-testid="new-invoice-btn"><Plus className="w-4 h-4 mr-1.5" /> Generate Monthly Invoice</Button></SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md">
              <SheetHeader><SheetTitle>Generate monthly invoice</SheetTitle></SheetHeader>
              <form onSubmit={generate} className="mt-6 space-y-4" data-testid="new-invoice-form">
                <div className="text-sm text-zinc-600 leading-relaxed border-l-2 border-zinc-300 pl-3">Aggregates every <span className="font-mono">approved</span> production hour for the selected client during the chosen month, using the client's hourly rate.</div>
                <div><Label>Client</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger data-testid="inv-client"><SelectValue placeholder="Choose client" /></SelectTrigger>
                    <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.hourly_rate ? ` · ₹${c.hourly_rate}/hr` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label htmlFor="i-month">Month (YYYY-MM)</Label><Input id="i-month" type="month" value={form.period_month} onChange={(e) => setForm({ ...form, period_month: e.target.value })} data-testid="inv-month" /></div>
                <div><Label htmlFor="i-tax">Tax %</Label><Input id="i-tax" type="number" value={form.tax_percent} onChange={(e) => setForm({ ...form, tax_percent: e.target.value })} data-testid="inv-tax" /></div>
                <SheetFooter><Button type="submit" disabled={genMut.isPending} data-testid="inv-submit">Generate Draft</Button></SheetFooter>
              </form>
            </SheetContent>
          </Sheet>
        }
      />
      <div className="max-w-[1400px] mx-auto px-8 py-6">
        <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden" data-testid="invoices-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3">Number</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Period</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3 text-right">Paid</th><th className="px-4 py-3 text-right">Balance</th><th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((inv) => (
                <tr key={inv.id} data-testid={`inv-row-${inv.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{inv.number}</td>
                  <td className="px-4 py-3 font-medium">{inv.client_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{inv.period_month || "—"}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{inv.status}</Badge></td>
                  <td className="px-4 py-3 text-right font-mono">{INR(inv.total)}</td>
                  <td className="px-4 py-3 text-right font-mono">{INR(inv.amount_paid)}</td>
                  <td className="px-4 py-3 text-right font-mono text-amber-700">{INR(inv.balance_due)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-2 justify-end">
                      {inv.status === "draft" && <Button size="sm" variant="outline" onClick={() => sendMut.mutate(inv.id)} data-testid={`inv-send-${inv.id}`}><Send className="w-3 h-3 mr-1" /> Send</Button>}
                      <Link to={`/app/invoices/${inv.id}`} className="text-xs inline-flex items-center gap-1 hover:text-zinc-900" data-testid={`inv-open-${inv.id}`}>Open <ExternalLink className="w-3 h-3" /></Link>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-zinc-500">No invoices yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function InvoiceDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [payForm, setPayForm] = useState({ amount: "", method: "bank_transfer", reference: "", paid_at: new Date().toISOString().slice(0, 10), notes: "" });

  const { data, isLoading } = useQuery({ queryKey: ["invoice", id], queryFn: async () => (await api.get(`/invoices/${id}`)).data });

  const payMut = useMutation({
    mutationFn: (p) => api.post("/payments", p),
    onSuccess: () => {
      toast.success("Payment recorded");
      setPayForm({ amount: "", method: "bank_transfer", reference: "", paid_at: new Date().toISOString().slice(0, 10), notes: "" });
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (isLoading || !data) return <div className="p-12 text-sm text-zinc-500 font-mono">Loading invoice…</div>;
  const { invoice: inv, client, payments } = data;

  return (
    <div data-testid="invoice-detail">
      <PageHeader
        eyebrow={<Link to="/app/invoices" className="hover:underline inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All Invoices</Link>}
        title={<span className="flex items-center gap-3">{inv.number} <Badge variant="outline" className="capitalize">{inv.status}</Badge></span>}
        description={<span>{client?.name} · Period {inv.period_month || "—"}</span>}
      />
      <div className="max-w-[1400px] mx-auto px-8 py-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-zinc-200 rounded-lg bg-white p-6" data-testid="inv-lines">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Line Items</div>
          <table className="w-full text-sm mt-4">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-100">
              <tr><th className="text-left py-2">Description</th><th className="text-right py-2 w-24">Qty</th><th className="text-right py-2 w-24">Rate</th><th className="text-right py-2 w-32">Amount</th></tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {(inv.lines || []).map((l, i) => (
                <tr key={`${l.source_kind || "line"}-${i}-${l.description}`}>
                  <td className="py-2">{l.description}<div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mt-0.5">source · {l.source_kind}</div></td>
                  <td className="py-2 text-right font-mono">{l.quantity} {l.unit}</td>
                  <td className="py-2 text-right font-mono">₹{l.rate}</td>
                  <td className="py-2 text-right font-mono">{INR(l.quantity * l.rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-6 ml-auto max-w-xs space-y-1 text-sm">
            <div className="flex justify-between text-zinc-500"><span>Subtotal</span><span className="font-mono">{INR(inv.subtotal)}</span></div>
            <div className="flex justify-between text-zinc-500"><span>Tax ({inv.tax_percent}%)</span><span className="font-mono">{INR(inv.tax_amount)}</span></div>
            <div className="flex justify-between border-t border-zinc-200 pt-1 font-bold"><span>Total</span><span className="font-mono">{INR(inv.total)}</span></div>
            <div className="flex justify-between text-emerald-700 mt-2"><span>Paid</span><span className="font-mono">{INR(inv.amount_paid)}</span></div>
            <div className="flex justify-between text-amber-700 font-bold"><span>Balance Due</span><span className="font-mono">{INR(inv.balance_due)}</span></div>
          </div>
        </div>
        <div className="space-y-4">
          {inv.status !== "paid" && (
            <div className="border border-zinc-200 rounded-lg bg-white p-5" data-testid="record-payment-card">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Record a Payment</div>
              <form onSubmit={(e) => { e.preventDefault(); payMut.mutate({ ...payForm, invoice_id: id, amount: Number(payForm.amount) }); }} className="mt-3 space-y-3" data-testid="record-payment-form">
                <div><Label htmlFor="p-amt">Amount</Label><Input id="p-amt" type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} required data-testid="pay-amount" /></div>
                <div><Label>Method</Label>
                  <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                    <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="bank_transfer">Bank Transfer</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="cheque">Cheque</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label htmlFor="p-ref">Reference</Label><Input id="p-ref" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} data-testid="pay-reference" /></div>
                <div><Label htmlFor="p-date">Paid on</Label><Input id="p-date" type="date" value={payForm.paid_at} onChange={(e) => setPayForm({ ...payForm, paid_at: e.target.value })} data-testid="pay-date" /></div>
                <Button type="submit" className="w-full" disabled={payMut.isPending} data-testid="pay-submit">Record Payment</Button>
              </form>
            </div>
          )}
          <div className="border border-zinc-200 rounded-lg bg-white p-5" data-testid="payments-history">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Payments · {(payments || []).length}</div>
            <ul className="mt-3 space-y-2 text-sm">
              {(payments || []).map((p) => (
                <li key={p.id} className="flex justify-between border-b border-zinc-100 pb-2">
                  <div><div className="font-medium">{INR(p.amount)}</div><div className="text-[11px] text-zinc-500 capitalize font-mono">{p.method.replace("_", " ")} {p.reference && `· ${p.reference}`}</div></div>
                  <div className="text-[11px] text-zinc-500 font-mono">{p.paid_at}</div>
                </li>
              ))}
              {(payments || []).length === 0 && <li className="text-sm text-zinc-500">No payments recorded.</li>}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
